<template>
  <div 
    class="PosterPage h-full" 
    :class="[
      `step--${step}`,
      { 'poster-bottom': step === 2, 'hide': hidePage }
    ]" 
    ref="root"
  >
    <div class="poster-item">
      <GLApp ref="gl" />
      <div class="poster-name">
        <input type="text" name="name" ref="nameInput" v-model="posterName" :autofocus="step === 2" />
      </div>
      <div class="poster-background">
        <img :src="resolvePath(require('@/assets/img/poster/wanted.jpg'))" alt="Poster Background" />
      </div>
    </div>
    <div class="poster-content">
      <template v-if="step === 0">
        <line-by-line tag="h1" class="h1" :delay="isBack ? 0 : 0.5">{{ $t('poster.advice') }}</line-by-line>
        <primary-btn class="take-btn" @click="takePicture">
          {{ $t('poster.take_picture') }}
        </primary-btn>
      </template>
      <template v-if="step === 1">
        <primary-btn @click="tryAgain">
          {{ $t('poster.try_again') }}
        </primary-btn>
        <primary-btn @click="goToText">
          {{ $t('poster.proceed') }}
        </primary-btn>
      </template>
      <template v-if="step === 2">
        <line-by-line tag="h1" class="h1" :delay="0.5">{{ $t('poster.pirate_name') }}</line-by-line>
        <line-by-line tag="h2" class="h2" :delay="1">{{ $t('poster.max_letters') }}</line-by-line>
        <primary-btn @click="generatePoster" :class="{ 'disabled': isDisabled }">
          {{ $t('poster.proceed') }}
        </primary-btn>
      </template>
    </div>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import GLApp from '@/webgl/GLApp.vue';
import { gsap } from 'gsap';
import { setPoster } from '@/store/modules/Poster';
import Router from '@/core/Router';
import Delay from '@/core/Delay';
import Viewport from '@/store/modules/Viewport';
import { resolvePath } from '@/utils/Path';
import { setUserName } from '@/store/modules/User';

const root = ref(null);
const gl = ref(null);
const step = ref(0);
const isBack = ref(false);
const takePicture = () => {
  gl.value.takePicture();
  step.value++;
}

const tryAgain = () => {
  gl.value.retakePicture();
  isBack.value = true;
  step.value = 0;
}

const goToText = () => {
  step.value = 2;
  nameInput.value.focus();
}

const fontSizeScale = [
  {
    letters: 7,
    fontSizeDesktop: 11.5,
    fontSizeMobile: 5.4
  }, {
    letters: 8,
    fontSizeDesktop: 10.1,
    fontSizeMobile: 4.7
  }, {
    letters: 9,
    fontSizeDesktop: 8.9,
    fontSizeMobile: 4.1
  }, {
    letters: 10,
    fontSizeDesktop: 8,
    fontSizeMobile: 3.7
  }, {
    letters: 11,
    fontSizeDesktop: 7.2,
    fontSizeMobile: 3.3
  }, {
    letters: 12,
    fontSizeDesktop: 6.6,
    fontSizeMobile: 3
  }, {
    letters: 13,
    fontSizeDesktop: 6.1,
    fontSizeMobile: 2.8
  }, {
    letters: 14,
    fontSizeDesktop: 5.6,
    fontSizeMobile: 2.6
  }, {
    letters: 15,
    fontSizeDesktop: 5.2,
    fontSizeMobile: 2.4
  }, {
    letters: 16,
    fontSizeDesktop: 5.2,
    fontSizeMobile: 2.4
  }
];

onMounted(() => {
  document.addEventListener('keydown', (e) => onKeyDown(e));
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', (e) => onKeyDown(e));
})

const onKeyDown = (e) => {
  if(e.key === 'Enter') {
    if(step.value === 0) {
      takePicture();
    } else if(step.value === 1) {
      goToText();
    } else if(step.value === 2) {
      generatePoster();
    }
  }

  if(e.key === ' ' && step.value === 0) {
    takePicture();
  }
}

const isDisabled = ref(true);
const posterName = ref('');
const maxLetters = 15;
const nameInput = ref(null);
watch(posterName, (value) => {
  isDisabled.value = value.length === 0 || value.length > maxLetters;
  
  // check if it's latin characters or number - allow accent + tiret
  if (!/^[-a-zA-Z0-9\u00C0-\u024F]+$/.test(value)) {
    posterName.value = value.slice(0, -1);
  }

  // block more than 15 letters
  if(value.length > maxLetters) {
    posterName.value = value.slice(0, maxLetters);
  }

  if(value.length >= 7) {
    const fontSize = Viewport.isMobile ? fontSizeScale.find((item) => item.letters === value.length).fontSizeMobile : fontSizeScale.find((item) => item.letters === value.length).fontSizeDesktop;
    nameInput.value.style.fontSize = `${fontSize}vh`;
  } else {
    nameInput.value.removeAttribute('style');
  }
});

const hidePage = ref(false);
const generatePoster = async() => {
  if(posterName.value.length === 0 || posterName.value.length > maxLetters) return;

  hidePage.value = true;
  await Delay(250);
  setUserName(posterName.value);
  gl.value.generatePoster(posterName.value);
  setPoster(gl.value.URLPoster);
  Router.push({ name: 'ShowPoster' });
}
</script>

<style lang="stylus" scoped>
.PosterPage
  position relative
  width 100%
  display flex
  align-items center
  flex-direction column
  gap rem(52)
  transition opacity 0.5s
  &.hide
    opacity 0

.poster-background
  position absolute
  top -4vh
  left 50%
  transform translateX(-50%)
  mask-image linear-gradient(180deg, #d9d9d9 60%, rgba(217,217,217,0) 68%)
  z-index 1
  +mobile()
    top 20vh
    mask-image linear-gradient(180deg, #d9d9d9 60%, rgba(217,217,217,0) 72%)

  .poster-bottom &
    mask-image linear-gradient(0deg, #d9d9d9 60%, rgba(217,217,217,0) 100%)

  &--blend
    position absolute
    inset 0
    width 100%
    height calc(100% - 5px)
    background #C2B69C
    mix-blend-mode soft-light
    z-index 1

  img
    width 85vh
    filter brightness(1.1)
    +mobile()
      width 40vh

.glapp
  margin-top: 28.2vh;
  margin-left: 0.7vh;
  +mobile()
    margin-top 35.2vh
    margin-left 0.4vh

.poster-item
  transform translateY(0)
  transition transform 1s cubic-bezier(0.55, 0.05, 0.37, 1.01)
  .poster-bottom &
    transform translateY(-80%)
    +mobile()
      transform translateY(-40%)

.poster-content
  position absolute
  left 50%
  bottom 8vh
  transform translateX(-50%)
  text-align center
  width 100%
  z-index 2

  .take-btn
    margin-top 2vh

  .primary-btn
    width rem(300)
    & + .primary-btn
      margin-left 0
      +mobile()
        margin-left 0
        margin-top 0

    :deep(.primary-btn--text)
      display flex
      align-items center
      justify-content center

  .h2
    margin 2vh 0
  +mobile()
    .h1
      max-width 75%
      text-align center
      margin 0 auto
    .step--1 &
      display flex
      flex-direction column
      align-items center
      justify-content center

.poster-name
  position absolute
  bottom -29.25vh
  width 85%
  left 50%
  transform translateX(-50%)
  z-index 2
  opacity 0
  pointer-events none
  transition opacity 0.25s 0.5s ease-out
  +mobile()
   bottom -13vh
  .poster-bottom &
    opacity 1
    pointer-events auto

  input
    background none
    full()
    font-family var(--tertiary-font)
    font-size 13.5vh
    font-weight bold
    height 15vh
    line-height 1
    background rgba(251, 237, 208, 0.25)
    color var(--black)
    text-transform uppercase
    text-align center
    +mobile()
      font-size 6.4vh
      height 6.5vh
</style>